import { Table } from '@chakra-ui/react';
import { useLayoutEffect, useRef, useState } from 'react';
import type { ComponentProps, CSSProperties, PropsWithChildren } from 'react';

const OVERFLOW_EPSILON_PX = 1;

/**
 * The inline padding of a table's first and last cells, so the outer columns
 * never touch the window edge the table bleeds to. A table whose last visible
 * column changes with the breakpoint gives this padding to that column itself.
 */
export const TABLE_EDGE_PADDING_X = '1.5rem';

// Sticky columns separate from the scrolling ones with a real hairline on
// their inner side, never a shadow.
const stickyStartCellBase = {
  position: 'sticky',
  bg: 'white',
  backgroundClip: 'padding-box',
  borderInlineEnd: '1px solid',
  borderInlineEndColor: 'cardBorder',
};

const stickyStartHeaderBase = {
  ...stickyStartCellBase,
  bg: 'background',
  zIndex: 5,
};

const stickyEndCellBase = {
  position: 'sticky',
  bg: 'white',
  backgroundClip: 'padding-box',
  borderInlineStart: '1px solid',
  borderInlineStartColor: 'cardBorder',
};

const stickyEndHeaderBase = {
  ...stickyEndCellBase,
  bg: 'background',
  zIndex: 5,
};

export const TableScrollArea = ({
  children,
  style,
  ...props
}: PropsWithChildren<ComponentProps<typeof Table.ScrollArea>>) => {
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;

    const updateOverflowState = () => {
      const nextIsOverflowing =
        scrollArea.scrollWidth - scrollArea.clientWidth > OVERFLOW_EPSILON_PX;
      setIsOverflowing((current) => (current === nextIsOverflowing ? current : nextIsOverflowing));
    };

    updateOverflowState();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateOverflowState);
      return () => window.removeEventListener('resize', updateOverflowState);
    }

    const resizeObserver = new ResizeObserver(updateOverflowState);
    resizeObserver.observe(scrollArea);

    const table = scrollArea.querySelector('table');
    if (table) {
      resizeObserver.observe(table);
    }

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <Table.ScrollArea
      ref={scrollAreaRef}
      data-overflowing={isOverflowing ? 'true' : undefined}
      overflowX="auto"
      overflowY="visible"
      overscrollBehaviorX="contain"
      style={{
        width: 'calc(100% + var(--table-bleed-total))',
        maxWidth: 'calc(100% + var(--table-bleed-total))',
        marginInlineStart: 'calc(var(--table-bleed-x) * -1)',
        marginInlineEnd: 'calc(var(--table-bleed-x) * -1)',
        ...style,
      }}
      css={{
        '--table-bleed-x': '1rem',
        '--table-bleed-total': '2rem',
        '@media (min-width: 80rem)': {
          '--table-bleed-x': '1.5rem',
          '--table-bleed-total': '3rem',
        },
        '& table': {
          width: '100%',
          minWidth: 'var(--table-min-width, max-content)',
        },
        '& th:first-of-type, & td:first-of-type': {
          paddingInlineStart: TABLE_EDGE_PADDING_X,
        },
        '& th:last-of-type, & td:last-of-type': {
          paddingInlineEnd: TABLE_EDGE_PADDING_X,
        },
        '& [data-sticky="start"], & [data-sticky="end"]': {
          minWidth: 'var(--sticky-width)',
          width: 'var(--sticky-width)',
          maxWidth: 'var(--sticky-width)',
        },
        '& [data-sticky-divider="end"]': {
          borderInlineEnd: '1px solid',
          borderInlineEndColor: 'cardBorder',
          paddingInlineStart: '0.875rem',
          paddingInlineEnd: '0.875rem',
        },
        '& [data-sticky-divider="end"]:first-of-type': {
          paddingInlineStart: 'var(--table-selection-padding-start, 0.875rem)',
          paddingInlineEnd: '0.875rem',
        },
        '& thead th': {
          position: 'sticky',
          top: 'var(--table-sticky-offset, 0px)',
          zIndex: 4,
          bg: 'background',
          backgroundClip: 'padding-box',
          borderBottom: '1px solid',
          borderBottomColor: 'cardBorder',
        },
        // Every dashboard data table reads at the same 60px row. It lives here
        // rather than on the recipe's `sm` size because compact `sm` tables
        // (markdown replies, checkout summaries, the CSV preview) must keep
        // their tight rows. A cell height is a floor: taller content still grows.
        '& tbody td': {
          height: '60px',
        },
        '&[data-overflowing="true"] [data-sticky="start"]': {
          ...stickyStartCellBase,
          left: 'var(--sticky-left, 0px)',
        },
        '&[data-overflowing="true"] [data-sticky="end"]': {
          ...stickyEndCellBase,
          right: 'var(--sticky-right, 0px)',
        },
        '&[data-overflowing="true"] thead [data-sticky="start"]': {
          ...stickyStartHeaderBase,
        },
        '&[data-overflowing="true"] thead [data-sticky="end"]': {
          ...stickyEndHeaderBase,
        },
        '&[data-overflowing="true"] tbody tr:not([data-table-empty-row]) [data-sticky]': {
          zIndex: 2,
        },
        // Same colour as the row's own hover so the row reads as one band.
        '&[data-overflowing="true"] tbody tr:not([data-table-empty-row]):hover [data-sticky]': {
          bg: 'rowHoverBg',
        },
      }}
      {...props}
    >
      {children}
    </Table.ScrollArea>
  );
};

interface StickyColumnOptions {
  divider?: 'end';
}

export const stickyColumnProps = (width: string, left = '0px', options?: StickyColumnOptions) => ({
  'data-sticky': 'start',
  'data-sticky-divider': options?.divider,
  style: {
    '--sticky-left': left,
    '--sticky-width': width,
  } as CSSProperties,
});

export const stickyEndColumnProps = (width: string, right = '0px') => ({
  'data-sticky': 'end',
  style: {
    '--sticky-right': right,
    '--sticky-width': width,
  } as CSSProperties,
});
